import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  Wallet, ArrowLeftRight, Link2, ShieldCheck, Plus, ArrowDownRight,
  Key, TrendingUp, Eye, MessageSquare, Upload, Package2, Store,
  BarChart3, CheckCircle2, XCircle, Clock, ArrowUpRight, Zap,
  CreditCard, Users
} from 'lucide-react';
import { canCreatePaymentLinks, canUseApiKeys, canUseWithdrawals } from '../../lib/accountPermissions';

function formatAmount(amount, currency) {
  if (amount == null) return `${currency} 0`;
  return `${currency} ${Number(amount).toLocaleString('en-US', { minimumFractionDigits: 0 })}`;
}

function StatusBadge({ status }) {
  const map = {
    successful: 'badge-success',
    paid:       'badge-success',
    approved:   'badge-info',
    pending:    'badge-pending',
    failed:     'badge-failed',
    rejected:   'badge-failed',
  };
  return (
    <span className={`badge ${map[status] || 'badge-default'} capitalize`}>
      {status}
    </span>
  );
}

function SettlementBadge({ txn }) {
  const mode   = String(txn?.settlement_mode || '').toLowerCase();
  const status = String(txn?.settlement_status || '').toLowerCase();
  if (txn?.settled_directly_to_merchant || mode === 'flutterwave_split') {
    const label = status === 'pending' ? 'Split pending' : 'Split-settled';
    return <span className="badge badge-info">{label}</span>;
  }
  if (mode === 'jjuma_wallet') {
    const label = status === 'pending_wallet_fallback' ? 'Wallet pending' : 'JJuma wallet';
    return <span className="badge badge-success">{label}</span>;
  }
  if (mode === 'manual') {
    return <span className="badge badge-pending">Manual review</span>;
  }
  return null;
}

/* ── Metric Card ─────────────────────────────────────────────── */
function MetricCard({ label, value, icon: Icon, iconClass, accent, sub, testId, delay }) {
  return (
    <div className={`stat-card stat-card-${accent} p-5 animate-slide-up`} style={{ animationDelay: delay }} data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{label}</p>
        <div className={`metric-icon metric-icon-${iconClass}`}>
          <Icon size={18} />
        </div>
      </div>
      <p className="text-2xl font-bold text-slate-900 mono-nums">{value}</p>
      {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
    </div>
  );
}

/* ── Quick Action Tile ───────────────────────────────────────── */
function QuickAction({ label, icon: Icon, onClick, color = 'emerald', testId }) {
  const colors = {
    emerald: 'hover:border-emerald-200 hover:bg-emerald-50/60 group-hover:text-emerald-700',
    blue:    'hover:border-blue-200 hover:bg-blue-50/60 group-hover:text-blue-600',
    violet:  'hover:border-violet-200 hover:bg-violet-50/60 group-hover:text-violet-600',
    amber:   'hover:border-amber-200 hover:bg-amber-50/60 group-hover:text-amber-600',
  };
  const iconColors = {
    emerald: 'text-slate-400 group-hover:text-emerald-700',
    blue:    'text-slate-400 group-hover:text-blue-600',
    violet:  'text-slate-400 group-hover:text-violet-600',
    amber:   'text-slate-400 group-hover:text-amber-600',
  };
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`group p-4 rounded-2xl bg-white border border-slate-200 shadow-sm transition-all duration-200 text-left ${colors[color]}`}
    >
      <Icon size={20} className={`mb-2 transition-colors duration-200 ${iconColors[color]}`} />
      <p className="text-sm font-semibold text-slate-700">{label}</p>
    </button>
  );
}

/* ── Analytics tile ─────────────────────────────────────────── */
function AnalyticsTile({ label, value, sub, icon: Icon, iconClass }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 flex items-start gap-3">
      <div className={`metric-icon metric-icon-${iconClass} shrink-0`}>
        <Icon size={16} />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-slate-500 font-medium">{label}</p>
        <p className="text-xl font-bold text-slate-900 mono-nums mt-0.5">{value}</p>
        {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

export default function Overview() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/dashboard/overview').then(r => {
      setData(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-[3px] border-emerald-700 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-slate-400">Loading dashboard…</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <XCircle size={40} className="mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500 font-medium">Failed to load dashboard data.</p>
          <button onClick={() => window.location.reload()} className="mt-2 text-sm text-emerald-700 hover:underline">Retry</button>
        </div>
      </div>
    );
  }

  const primaryWallet = data.wallets?.find(w => w.currency === (user?.default_currency || 'UGX')) || data.wallets?.[0] || {};
  const currency      = primaryWallet.currency || 'UGX';
  const canCreate     = canCreatePaymentLinks(user);
  const canWithdraw   = canUseWithdrawals(user);
  const canApiKeys    = canUseApiKeys(user);

  return (
    <div className="space-y-6 animate-fade-in">

      {/* ── Header ─────────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1
            className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Welcome back, <span className="text-emerald-700">{user?.name?.split(' ')[0]}</span> 👋
          </h1>
          <p className="text-slate-500 text-sm mt-1 flex items-center gap-1.5">
            <span className="pulse-dot" />
            Your JJuma Pay overview is live
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button
            variant="outline"
            onClick={() => navigate('/dashboard/whatsapp-templates')}
            className="border-slate-200 text-slate-600 hidden sm:flex"
          >
            <MessageSquare size={15} className="mr-1.5" /> WhatsApp
          </Button>
          <Button
            onClick={() => canCreate ? navigate('/dashboard/payment-links') : navigate('/dashboard/verification')}
            className="bg-emerald-700 hover:bg-emerald-800 text-white shadow-sm"
            data-testid="quick-create-link-btn"
          >
            <Plus size={16} className="mr-1.5" /> Create Link
          </Button>
        </div>
      </div>

      {/* ── API notice ─────────────────────────────────────────── */}
      {user?.account_type === 'individual' && !canApiKeys && (
        <div className="flex items-start gap-3 p-4 rounded-2xl border border-amber-200 bg-amber-50 text-amber-900 text-sm">
          <Zap size={16} className="mt-0.5 shrink-0 text-amber-600" />
          <span>API access is available for verified business accounts only.</span>
        </div>
      )}

      {/* ── Primary wallet metrics ─────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          label="Total Balance"
          value={formatAmount(primaryWallet.total_balance, currency)}
          icon={Wallet}
          iconClass="emerald"
          accent="emerald"
          delay="0ms"
          testId="total-balance-value"
        />
        <MetricCard
          label="Available"
          value={formatAmount(primaryWallet.available_balance, currency)}
          icon={TrendingUp}
          iconClass="teal"
          accent="teal"
          sub={primaryWallet.pending_balance > 0 ? `${formatAmount(primaryWallet.pending_balance, currency)} pending` : null}
          delay="60ms"
          testId="available-balance-value"
        />
        <MetricCard
          label="Total Transactions"
          value={data.total_transactions?.toLocaleString() || '0'}
          icon={ArrowLeftRight}
          iconClass="violet"
          accent="violet"
          delay="120ms"
          testId="total-transactions-value"
        />
        <MetricCard
          label="Active Links"
          value={data.active_links?.toLocaleString() || '0'}
          icon={Link2}
          iconClass="amber"
          accent="amber"
          delay="180ms"
          testId="active-links-value"
        />
      </div>

      {/* ── Analytics section ──────────────────────────────────── */}
      {data.analytics && (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <BarChart3 size={16} className="text-slate-400" />
            <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wide">Sales Analytics</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3">
            <AnalyticsTile
              label="Today"
              value={formatAmount(data.analytics.today_sales?.total || 0, currency)}
              sub={`${data.analytics.today_sales?.count || 0} payments`}
              icon={CheckCircle2}
              iconClass="emerald"
            />
            <AnalyticsTile
              label="This Week"
              value={formatAmount(data.analytics.week_sales?.total || 0, currency)}
              sub={`${data.analytics.week_sales?.count || 0} payments`}
              icon={TrendingUp}
              iconClass="blue"
            />
            <AnalyticsTile
              label="This Month"
              value={formatAmount(data.analytics.month_sales?.total || 0, currency)}
              sub={`${data.analytics.month_sales?.count || 0} payments`}
              icon={BarChart3}
              iconClass="violet"
            />
            <AnalyticsTile
              label="Pending"
              value={data.analytics.pending_payments || 0}
              icon={Clock}
              iconClass="amber"
            />
            <AnalyticsTile
              label="Paid Invoices"
              value={data.analytics.paid_invoices || 0}
              icon={CreditCard}
              iconClass="teal"
            />
            <AnalyticsTile
              label="Store Orders"
              value={data.analytics.store_orders || 0}
              icon={Store}
              iconClass="emerald"
            />
          </div>

          {/* Best selling + payment methods */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
              <CardHeader className="pb-3 flex flex-row items-center justify-between">
                <CardTitle className="text-base font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Best Selling Products
                </CardTitle>
                <Package2 size={16} className="text-slate-400" />
              </CardHeader>
              <CardContent>
                {data.analytics.best_selling_products?.length ? (
                  <div className="space-y-2">
                    {data.analytics.best_selling_products.map((item, i) => (
                      <div key={`${item.product_name}-${i}`} className="flex items-center justify-between gap-3 py-2 border-b border-slate-50 last:border-0">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <span className="text-xs font-bold text-slate-400 w-4">#{i + 1}</span>
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-slate-900 truncate">{item.product_name}</p>
                            <p className="text-xs text-slate-400">{item.quantity} sold</p>
                          </div>
                        </div>
                        <p className="text-sm font-bold text-emerald-700 mono-nums shrink-0">{formatAmount(item.revenue, currency)}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="py-8 text-center">
                    <Package2 size={32} className="mx-auto text-slate-200 mb-2" />
                    <p className="text-sm text-slate-400">No sales data yet</p>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
              <CardHeader className="pb-3 flex flex-row items-center justify-between">
                <CardTitle className="text-base font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Payment Methods Used
                </CardTitle>
                <CreditCard size={16} className="text-slate-400" />
              </CardHeader>
              <CardContent>
                {data.analytics.payment_methods_used?.length ? (
                  <div className="space-y-2">
                    {data.analytics.payment_methods_used.map((item, i) => (
                      <div key={`${item.method}-${i}`} className="flex items-center justify-between gap-3 py-2 border-b border-slate-50 last:border-0">
                        <p className="text-sm font-semibold text-slate-700 capitalize">{item.method?.replace(/_/g, ' ')}</p>
                        <span className="badge badge-default">{item.count}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="py-8 text-center">
                    <CreditCard size={32} className="mx-auto text-slate-200 mb-2" />
                    <p className="text-sm text-slate-400">No payment data yet</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* ── Multi-currency wallets ─────────────────────────────── */}
      {data.wallets?.length > 1 && (
        <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
          <CardHeader className="pb-3 flex flex-row items-center justify-between">
            <CardTitle className="text-base font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Wallet Balances</CardTitle>
            <Wallet size={16} className="text-slate-400" />
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {data.wallets.map(w => (
                <div key={w.currency} className="flex items-center justify-between p-4 rounded-2xl bg-slate-50 border border-slate-100">
                  <div>
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">{w.currency}</p>
                    <p className="text-xl font-bold text-slate-900 mono-nums mt-0.5" data-testid={`wallet-${w.currency}-balance`}>
                      {formatAmount(w.available_balance, w.currency)}
                    </p>
                  </div>
                  {w.pending_balance > 0 && (
                    <span className="badge badge-pending">
                      {formatAmount(w.pending_balance, w.currency)} pending
                    </span>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Quick actions ──────────────────────────────────────── */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Zap size={15} className="text-slate-400" />
          <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wide">Quick Actions</h2>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <QuickAction label="Create Link"   icon={Plus}        onClick={() => canCreate   ? navigate('/dashboard/payment-links')  : navigate('/dashboard/verification')} color="emerald" testId="quick-action-create-link" />
          <QuickAction label="Withdraw"      icon={ArrowDownRight} onClick={() => canWithdraw ? navigate('/dashboard/withdrawals')    : navigate('/dashboard/verification')} color="teal"    testId="quick-action-withdraw" />
          <QuickAction label="API Keys"      icon={Key}         onClick={() => canApiKeys  ? navigate('/dashboard/api-keys')       : navigate('/dashboard/verification')} color="violet"  testId="quick-action-api-keys" />
          <QuickAction label="Verification"  icon={ShieldCheck}  onClick={() => navigate('/dashboard/verification')} color="amber" testId="quick-action-verification" />
        </div>
      </div>

      {/* ── Quick tools bar ────────────────────────────────────── */}
      <div className="rounded-2xl border border-slate-200 bg-white p-4 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
        <div>
          <p className="text-sm font-bold text-slate-900">Quick Tools</p>
          <p className="text-xs text-slate-500 mt-0.5">Jump straight to WhatsApp templates and bulk payment links.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" size="sm" onClick={() => navigate('/dashboard/whatsapp-templates')} className="border-slate-200 text-slate-700">
            <MessageSquare size={14} className="mr-1.5" /> WhatsApp Templates
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={() => navigate('/dashboard/bulk-payment-links')} className="border-slate-200 text-slate-700">
            <Upload size={14} className="mr-1.5" /> Bulk Payment Links
          </Button>
        </div>
      </div>

      {/* ── Recent activity ────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent payments */}
        <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-base font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Recent Payments</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard/transactions')} className="text-emerald-700 hover:text-emerald-800 -mr-1" data-testid="view-all-transactions-btn">
              <Eye size={14} className="mr-1" /> View All <ArrowUpRight size={12} className="ml-0.5" />
            </Button>
          </CardHeader>
          <CardContent>
            {!data.recent_transactions?.length ? (
              <div className="py-8 text-center">
                <ArrowLeftRight size={32} className="mx-auto text-slate-200 mb-2" />
                <p className="text-sm text-slate-400">No payments yet</p>
                <button onClick={() => navigate('/dashboard/payment-links')} className="mt-2 text-xs text-emerald-700 hover:underline">Create a payment link →</button>
              </div>
            ) : (
              <div className="divide-y divide-slate-50">
                {data.recent_transactions.map((txn, i) => (
                  <div key={i} className="flex items-start justify-between py-3 gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-8 h-8 rounded-xl bg-emerald-50 flex items-center justify-center shrink-0">
                        <span className="text-xs font-bold text-emerald-700">{(txn.sender_name || 'U').charAt(0).toUpperCase()}</span>
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-slate-900 truncate">{txn.sender_name}</p>
                        <p className="text-xs text-slate-400">{txn.payment_link_slug || 'Direct'}</p>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-sm font-bold text-emerald-700 mono-nums">+{formatAmount(txn.amount, txn.currency)}</p>
                      <div className="flex flex-col items-end gap-1 mt-1">
                        <StatusBadge status={txn.status} />
                        <SettlementBadge txn={txn} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent withdrawals */}
        <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-base font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Recent Withdrawals</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard/withdrawals')} className="text-emerald-700 hover:text-emerald-800 -mr-1" data-testid="view-all-withdrawals-btn">
              <Eye size={14} className="mr-1" /> View All <ArrowUpRight size={12} className="ml-0.5" />
            </Button>
          </CardHeader>
          <CardContent>
            {!data.recent_withdrawals?.length ? (
              <div className="py-8 text-center">
                <Wallet size={32} className="mx-auto text-slate-200 mb-2" />
                <p className="text-sm text-slate-400">No withdrawals yet</p>
                {canWithdraw && (
                  <button onClick={() => navigate('/dashboard/withdrawals')} className="mt-2 text-xs text-emerald-700 hover:underline">Request a withdrawal →</button>
                )}
              </div>
            ) : (
              <div className="divide-y divide-slate-50">
                {data.recent_withdrawals.map((w, i) => (
                  <div key={i} className="flex items-center justify-between py-3 gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-8 h-8 rounded-xl bg-teal-50 flex items-center justify-center shrink-0">
                        <ArrowDownRight size={15} className="text-teal-600" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-slate-900 truncate">{w.bank_name || 'Mobile Money'}</p>
                        <p className="text-xs text-slate-400">{new Date(w.created_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-sm font-bold text-slate-900 mono-nums">{formatAmount(w.amount, w.currency)}</p>
                      <div className="mt-1"><StatusBadge status={w.status} /></div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
